import { useEffect, useRef, useState } from 'react';
import { useNotification } from '#root/hooks';
import type { MatchReservationDTO } from '#root/models/PrepaidMatchDTO';
import { isPluralReservation, reservationIdps, usd } from './matchFormat';

/** Cuánto dura el "Copiado" del botón */
const COPIED_MS = 2000;

/**
 * Mensaje que el viajero pega en su grupo de WhatsApp tras reservar. Por pedido: nombre, el link
 * de compra y `precio real + comisión neta`. Sin `idp`: el admin puede asignarle otro pedido del
 * mismo producto y lo identifica por viajero en nadmin/pedidos/pre; la referencia es la reserva.
 * El BE ya manda el color dentro de `detail` ("Color: Burgundy"): no se le antepone nada. La
 * fecha es la de la reserva, no la del viaje.
 */
export const buildReservationMessage = (reservation: MatchReservationDTO): string => {
  const { id, orders, delivery_date } = reservation;
  const plural = isPluralReservation(reservation);

  const lines =
    orders.length > 0
      ? orders.flatMap((order) => {
          const detail = (order.detail ?? '').replace(/\s+/g, ' ').trim();
          const head = [`${order.name}${order.quantity > 1 ? ` x${order.quantity}` : ''}`];
          if (detail) head.push(detail);
          if (order.packaging) head.push(`empaque: ${order.packaging}`);
          return [
            `• ${head.join(' / ')}`,
            ...(order.purchase_url ? [`Link: ${order.purchase_url}`] : []),
            `${usd(order.price)} + ${usd(order.earning)}`,
          ];
        })
      : // Una reserva puede venir sin tarjetas: entonces solo se cuentan los `idp` de `group_key`
        [
          `• ${reservationIdps(reservation).length} ${
            plural ? 'pedidos reservados' : 'pedido reservado'
          }`,
        ];

  return [
    `Hola, reservé ${plural ? 'estos Pedidos Match' : 'este Pedido Match'} en la app (reserva #${id}):`,
    ...lines,
    `Puedo ${plural ? 'entregarlos' : 'entregarlo'} el: ${delivery_date}`,
    `¿Me confirman para ${plural ? 'comprarlos' : 'comprarlo'}?`,
  ].join('\n');
};

/**
 * Copia `message` al portapapeles con toast y un "Copiado" de 2 s en el botón. Si el
 * navegador lo bloquea, avisa: el texto sigue visible y seleccionable donde se muestra.
 */
export const useCopyMessage = (message: string) => {
  const { createMessage } = useNotification();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      createMessage.success('Mensaje copiado');
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
    } catch (e) {
      createMessage.warning('No se pudo copiar. Selecciona el mensaje y cópialo.');
    }
  };

  return { copied, copy };
};
